import React, { ReactNode, useEffect, useRef } from 'react';
import { Box, FormHelperText } from '@mui/material';
import { getIn } from 'formik';

export const useDoranFormikBridge = (props: any) => {
    const form = props?.form ?? {};
    const field = props?.field ?? {};
    const {
        status,
        errors,
        touched,
        submitCount = 0,
        validateOnBlur,
        validateOnChange,
        validateField,
        setFieldValue,
        setStatus,
    } = form;
    const name = field?.name ?? props?.name;
    const value = field?.value;

    const fieldTouched = getIn(touched, name);
    const fieldError = getIn(errors, name);
    const fieldStatus = getIn(status, name);
    const deferValidationUntilSubmit =
        validateOnBlur === false && validateOnChange === false;
    const canShowValidation = deferValidationUntilSubmit
        ? submitCount > 0
        : Boolean(fieldTouched);
    const invalid =
        canShowValidation && (Boolean(fieldError) || Boolean(fieldStatus));
    const helperText =
        props?.helpertext ??
        props?.helperText ??
        (canShowValidation ? fieldError || fieldStatus : '');

    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (!name || typeof validateField !== 'function') return;
        if (deferValidationUntilSubmit && submitCount === 0) return;

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => validateField(name), 350);
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [
        value,
        name,
        validateField,
        deferValidationUntilSubmit,
        submitCount,
    ]);

    const clearStatus = () => {
        if (!name || !status || !status[name] || !setStatus) return;
        const { [name]: _ignored, ...nextStatus } = status;
        setStatus(nextStatus);
    };

    const setValue = (nextValue: any) => {
        if (!name || !setFieldValue) return;
        setFieldValue(name, nextValue);
        clearStatus();
        props?.onChange?.(nextValue);
    };

    return {
        name,
        value,
        invalid,
        helperText,
        setValue,
        clearStatus,
        onBlur: field?.onBlur,
    };
};

export const DoranFieldShell = ({
    children,
    invalid,
    helperText,
}: {
    children: ReactNode;
    invalid?: boolean;
    helperText?: ReactNode;
}) => (
    <Box
        className={`project-doran-field${
            invalid ? ' project-doran-field--invalid' : ''
        }`}
        sx={{ width: '100%' }}>
        {children}
        {helperText ? (
            <FormHelperText error={Boolean(invalid)} sx={{ mx: 1.75, mt: 0.5 }}>
                {helperText}
            </FormHelperText>
        ) : null}
    </Box>
);
